// src/lib/accueil-urgence.js — L'ORDRE DE « CE QUI VOUS ATTEND ».
//
// Règle Dimitri, 2026-09-07 :
//   « Par urgence, pas par source. Une caution qui manque à J-1 passe avant un
//     barème absent. Ce n'est pas parce que ça vient de toi que c'est plus
//     urgent — ni l'inverse. Trie par ce qui coûte le plus cher si je le rate. »
//
// ── POURQUOI UN MODULE À PART ───────────────────────────────────────────────
// Un tri par « importance » est une OPINION. Écrite au fil du rendu, elle
// devient invisible et personne ne peut plus la contester. Ici elle est
// nommée, chiffrée, et testée — on peut être en désaccord avec une ligne du
// barème, et le dire.
//
// ── LE BARÈME : « QUE COÛTE-T-IL DE LE RATER ? » ────────────────────────────
// Petit nombre = cher à rater = haut de la liste. La source (opérateur ou
// application) N'ENTRE PAS dans le calcul : elle ne fait que s'afficher.
//
// Trois paliers, et une raison pour chaque rang :
//
//  0-9   IRRÉVERSIBLE OU QUELQU'UN ATTEND SUR PLACE
//        Un voyageur dans le logement, un logement sale à l'arrivée, une date
//        qui bouge et peut créer un double. Raté, ça ne se rattrape pas.
//
//  10-19 CHER MAIS RATTRAPABLE, AVEC UNE FENÊTRE
//        De l'argent récupérable, un voyageur ignoré, une réputation. Raté
//        aujourd'hui, ça coûte plus cher demain.
//
//  20-29 HYGIÈNE ET POTENTIEL
//        Rien ne casse aujourd'hui. Un barème absent bloque des réservations
//        qui n'existent pas encore ; un ménage jamais clos fausse un tableau.
const COUT = Object.freeze({
  // ── 0-9 · irréversible / quelqu'un attend sur place ──────────────────────
  // Une fermeture de dates refusée par une plateforme (ou sans réponse) :
  // tant qu'elle n'est pas reprise, quelqu'un peut réserver les mêmes nuits
  // (double réservation). Tranché par la maquette NEST v819 : en tête
  // d'« Urgent ». À égalité avec le signalement, la clé départage
  // (« fermeture: » avant « signalement: »).
  fermeture_a_reprendre: 0,
  signalement_voyageur: 0, // il est DANS le logement, maintenant
  menage_sans_agent: 1, // le suivant arrive dans un logement non fait
  depart_reporte: 2, // une date qui bouge peut créer un double
  immobilisee: 3, // argent du voyageur retenu au-delà du délai promis
  remboursement_en_retard: 3,
  formulaire_jamais_envoye: 4, // notre manquement, pas le sien
  // ── 10-19 · cher mais rattrapable ────────────────────────────────────────
  formulaire_non_rempli: 10, // remonté à 4 si l'arrivée est sous 48 h (voir plus bas)
  avis_negatif_non_traite: 11, // la fenêtre pour répondre est courte
  fil_sans_reponse: 12, // un voyageur ignoré ; le grand âge le fait remonter
  demande_horaire: 13,
  incident_a_trancher: 14,
  aircover_a_saisir: 15, // fenêtre AirCover : de l'argent qui s'évapore
  prelevee_sans_motif: 16, // indéfendable si on le demande, mais rien ne bouge
  rachat_jamais_fait: 17,
  incident_jamais_juge: 17,
  // ── 20-29 · hygiène et potentiel ─────────────────────────────────────────
  reprise_en_attente: 20,
  controle_sans_personne: 21,
  menage_jamais_clos: 22,
  depart_sans_menage: 22,
  montants_absents: 23,
  rendue_sans_prevenir: 23,
  logement_sans_bareme: 24, // bloque des réservations qui n'existent pas encore
  prospect_abandonne: 25,
});

// Ce qui n'est pas au barème passe APRÈS tout le reste, sans jamais disparaître.
// Un rang inventé mettrait une ligne inconnue au hasard ; un rang « dernier »
// la garde visible et signale qu'elle manque au barème.
const COUT_INCONNU = 90;

// Une échéance PROCHE remonte la ligne d'un palier entier : c'est la règle de
// Dimitri (« une caution qui manque à J-1 passe avant un barème absent »).
// N'agit QUE sur les familles porteuses d'une échéance — pour un retard, un
// petit nombre de jours veut dire l'inverse (c'est récent, donc moins grave).
const A_ECHEANCE = new Set(["formulaire_non_rempli", "formulaire_jamais_envoye"]);
const JOURS_IMMINENT = 2;

// ── L'ARGENT QUI DORT REMONTE AVEC LE TEMPS ─────────────────────────────────
//
// Nuance Dimitri, 2026-09-07 :
//   « Une caution bloquée depuis cent jours abîme autant ma réputation qu'un
//     voyageur mécontent aujourd'hui. Plus une caution dort, plus elle remonte.
//     Au-delà d'un mois, elle doit passer devant ce qui bloque des
//     réservations. »
//
// C'est le CONTRAIRE d'une échéance : ici un GRAND nombre de jours aggrave.
// Sans ce palier mobile, une caution rendue sans prévenir resterait à 23 —
// derrière un barème absent (24) de justesse — pendant cent jours.
//
// Les seuils, et ce qu'ils font franchir :
//   ≥ 30 j → 12 : passe devant TOUT le palier hygiène, donc devant ce qui
//                 bloque des réservations (24). C'est la demande, au mot.
//   ≥ 60 j → 6  : entre dans le palier des choses qu'on ne laisse plus traîner.
//   ≥ 100 j → 2 : juste derrière « un voyageur sur place » (0) et « un logement
//                 pas fait » (1) — les deux seules urgences physiques. C'est la
//                 lecture de « autant qu'un voyageur mécontent aujourd'hui »
//                 sans prétendre qu'un virement vaut quelqu'un qui attend
//                 devant une porte.
const ARGENT_QUI_DORT = new Set([
  "immobilisee",
  "remboursement_en_retard",
  "prelevee_sans_motif",
  "rendue_sans_prevenir",
  "montants_absents",
]);
const PALIERS_ARGENT = [
  { jours: 100, rang: 2 },
  { jours: 60, rang: 6 },
  { jours: 30, rang: 12 },
];

/** La clé de barème d'une ligne : son `type` (veille) ou sa `nature` (app). */
function cle(l) {
  return (l && (l.type || l.nature)) || null;
}

/**
 * coutSiRate — le rang d'une ligne. Petit = cher à rater.
 * @returns {number}
 */
function coutSiRate(ligne) {
  const k = cle(ligne);
  let base = Object.prototype.hasOwnProperty.call(COUT, k) ? COUT[k] : COUT_INCONNU;
  if (A_ECHEANCE.has(k)) {
    const j = ligne && Number(ligne.jours);
    // `jours` = combien de jours AVANT l'échéance. Sous le seuil, la ligne
    // remonte au palier « irréversible » : demain il sera trop tard.
    if (Number.isFinite(j) && j <= JOURS_IMMINENT) base = Math.min(base, 5);
  }
  if (ARGENT_QUI_DORT.has(k)) {
    const j = ligne && Number(ligne.jours);
    // Ici `jours` est un ÂGE : plus il est grand, plus c'est grave. On ne
    // descend JAMAIS une ligne — on ne fait que la remonter (`Math.min`), pour
    // qu'une famille déjà haute (l'argent retenu, rang 3) ne soit pas dégradée
    // par un âge modeste.
    if (Number.isFinite(j)) {
      for (const p of PALIERS_ARGENT) {
        if (j >= p.jours) { base = Math.min(base, p.rang); break; }
      }
    }
  }
  return base;
}

/**
 * trierParUrgence — copie triée. Départage stable et EXPLICITE :
 *   1. le coût si raté ;
 *   2. à coût égal, la plus ANCIENNE d'abord pour un retard, la plus PROCHE
 *      d'abord pour une échéance — les deux sens du mot « urgent » ;
 *   3. à égalité complète, la clé, pour que l'ordre ne bouge pas d'un rendu
 *      à l'autre (un écran qui se réordonne tout seul est illisible).
 */
function trierParUrgence(lignes) {
  return [...(Array.isArray(lignes) ? lignes : [])].sort((a, b) => {
    const d = coutSiRate(a) - coutSiRate(b);
    if (d !== 0) return d;
    const ae = A_ECHEANCE.has(cle(a));
    const be = A_ECHEANCE.has(cle(b));
    const ja = Number(a && a.jours);
    const jb = Number(b && b.jours);
    if (Number.isFinite(ja) && Number.isFinite(jb) && ae === be) {
      // Échéance : le plus PETIT nombre de jours d'abord. Retard : le plus GRAND.
      const r = ae ? ja - jb : jb - ja;
      if (r !== 0) return r;
    }
    return String((a && a.cle) || "").localeCompare(String((b && b.cle) || ""));
  });
}

module.exports = {
  COUT, COUT_INCONNU, coutSiRate, trierParUrgence,
  _internal: { cle, A_ECHEANCE, ARGENT_QUI_DORT, PALIERS_ARGENT },
};
